import * as React from "react";

import {
  Async,
  elementContains,
  ITheme,
  KeyCodes,
  memoizeFunction,
  mergeStyles,
} from "@fluentui/react";
import { Stack } from "@fluentui/react/lib/Stack";

import { isIdleCallbackEnabled } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import {
  computeNextShadowRenderingIncrement,
  withShadowRendering,
} from "@1js/acui-shadow-rendering";
import { AppThemeConsumer } from "@1js/acui-theme";

import {
  getAppGalleryStyles,
  getMergedGalleryAndButtonStyles,
  getStackTokens,
  stackStyles,
} from "../AppGallery";
import {
  AppGalleryButton,
  AppGalleryButtonOnExecuteParameter,
  AppGalleryButtonProps,
} from "../AppGalleryButton";
import {
  AppFullGalleryProps as AppFullGalleryExternalProps,
  AppFullGallerySection,
} from "./AppFullGallery.Props";

type AppFullGalleryProps<T extends AppGalleryButtonProps> =
  AppFullGalleryExternalProps<T> & {
    isShadowRendering?: boolean;
  };

export interface AppFullGalleryState {
  focusedIndex?: number;
  selectedId?: string;
  itemsToRender?: number;
}

/**
 * The Full Gallery is for rendering a gallery as a 2D grid
 * of gallery buttons with the given number of columns. Note,
 * the number of rows is automatically determined as the number of
 * galleryButtons / number of columns
 */
export class AppFullGalleryBase<
  T extends AppGalleryButtonProps
> extends React.PureComponent<AppFullGalleryProps<T>, AppFullGalleryState> {
  private navigationIdleTimeoutId: number | undefined;
  private isNavigationIdle: boolean;
  private readonly navigationIdleDelay: number = 250 /* ms */;
  private async: Async;

  private currentShadowRenderingStart: number | undefined;
  private totalShadowRenderingTime: number = 0;

  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: AppFullGalleryProps<T>) {
    super(props);

    this.isNavigationIdle = true;
    this.async = new Async(this);

    this.state = {
      selectedId: props.selectedId,
    };
  }

  public render() {
    const { id, positionInSet, level, gallerySections } = this.props;

    if (gallerySections) {
      return this.renderGallerySections(
        id,
        gallerySections,
        positionInSet,
        level
      );
    }
    /* eslint-disable-next-line no-console */
    console.warn("gallerySections must be defined");
    return null;
  }

  /**
   * Check new props for updated selected index. Update state if selected index has changed
   */
  public static getDerivedStateFromProps(
    newProps: AppFullGalleryProps<any>,
    state: AppFullGalleryState
  ) {
    const partialState: Partial<AppFullGalleryState> = {};
    if (newProps.selectedId !== state.selectedId) {
      partialState.selectedId = newProps.selectedId;
    }

    if (newProps.isShadowRendering && state.itemsToRender === undefined) {
      partialState.itemsToRender = 0;
    }

    return Object.keys(partialState).length > 0 ? partialState : null;
  }

  public componentDidMount() {
    this.renderMoreItemsInIdleCallback();
  }

  public componentDidUpdate() {
    this.renderMoreItemsInIdleCallback();
  }

  private getTotalGalleryButtons(): number {
    const { gallerySections, galleryButtons } = this.props;
    if (gallerySections) {
      return gallerySections.reduce(
        (prevSum: number, gallerySection: AppFullGallerySection<any>) => {
          return prevSum + gallerySection.galleryButtons.length;
        },
        0
      );
    }

    return galleryButtons!.length;
  }

  /**
   * This method increments the number of items to render in shadow mode. And these new items are mounted
   * in an idle callback.
   */
  private renderMoreItemsInIdleCallback = () => {
    if (
      this.props.isShadowRendering &&
      this.state.itemsToRender !== undefined
    ) {
      if (this.state.itemsToRender < this.getTotalGalleryButtons()) {
        if (this.currentShadowRenderingStart) {
          this.totalShadowRenderingTime +=
            performance.now() - this.currentShadowRenderingStart;
        }

        const nextIncrement = computeNextShadowRenderingIncrement(
          this.totalShadowRenderingTime,
          this.state.itemsToRender
        );

        isIdleCallbackEnabled() &&
          window.requestIdleCallback(
            () => {
              this.currentShadowRenderingStart = performance.now();
              // This state update is little tricky. Normally, for counters, we need to use the functional version
              // to not miss state updates. But here, we prefer to use this because we do not care if we miss
              // a few updates, but we do not want two state updates to combined in one render.
              this.setState({
                itemsToRender: this.state.itemsToRender! + nextIncrement,
              });
            },
            undefined /*options*/,
            `Gallery-${this.props.id}-ShadowRenderIncrement`
          );
      }
    }
  };

  private renderGalleryButtons(
    galleryButtons: T[],
    startingRenderRank?: number,
    positionInSet?: number,
    ariaLabel?: string
  ): JSX.Element {
    const buttonsToRender = this.getGalleryButtonsToRender(
      galleryButtons,
      startingRenderRank
    );

    // Pass in undefined selectedId for sections which do contain a selected gallery item
    // This helps us memoize better.
    let selectedId: string | undefined = undefined;
    if (buttonsToRender.some((button) => button.id === this.state.selectedId)) {
      selectedId = this.state.selectedId;
    }
    return this.renderGalleryStack(
      buttonsToRender,
      positionInSet,
      ariaLabel,
      selectedId
    );
  }

  private renderGalleryStack(
    buttonsToRender: T[],
    positionInSet?: number,
    ariaLabel?: string,
    selectedId?: string
  ): JSX.Element {
    const { id, defaultWidth, disabled, role } = this.props;
    const items = this.addPropsToGalleryButtonsArray(
      buttonsToRender,
      selectedId,
      disabled
    );
    return (
      <Stack
        key={`${id}-stack${positionInSet}`}
        aria-label={ariaLabel}
        styles={stackStyles}
        horizontal={true}
        wrap={true}
        tokens={getStackTokens(defaultWidth)}
        role={role}
      >
        {items.map((button) => this.renderGalleryButton(button))}
      </Stack>
    );
  }

  /**
   * Adds index, selected and disabled to gallery button props array.
   * Note that since we purify the Grid we use from Fabric, we need to ensure that
   * when we want the Grid to re-render, we give it a new reference.
   * So, this function has to contain all properties which can trigger a necessary re-render
   * as its arguments.
   * Currently, the only ones that do that are changes to selectedId and the whole gallery being disabled.
   * @param buttonsToRender
   */
  private addPropsToGalleryButtonsArray = memoizeFunction(
    (
      buttonsToRender: T[],
      selectedId?: string,
      galleryDisabled?: boolean
    ): any[] => {
      return buttonsToRender.map((button, index) =>
        this.addPropsToGalleryButton(button, index, selectedId, galleryDisabled)
      );
    }
  );

  /**
   * This adds the additional props to each gallery button.
   * Memoization is not needed here because each gallery button will do its shallow comparision
   * of props as they will be pure components.
   */
  private addPropsToGalleryButton(
    galleryButton: T,
    index?: number,
    selectedId?: string,
    galleryDisabled?: boolean
  ): any {
    return {
      ...galleryButton,
      disabled: galleryDisabled || galleryButton.disabled,
      index,
      selected: galleryButton.id === selectedId,
    };
  }

  /**
   * In shadow rendering mode, we might only want to render some of the gallery buttons in section,
   * This method takes care of that.
   */
  private getGalleryButtonsToRender(
    galleryButtons: T[],
    startingRenderRank: number | undefined
  ) {
    let changedGalleryButtons = galleryButtons;
    if (
      this.props.isShadowRendering &&
      this.state.itemsToRender !== undefined &&
      startingRenderRank !== undefined
    ) {
      if (this.state.itemsToRender <= startingRenderRank) {
        changedGalleryButtons = [];
      } else if (
        this.state.itemsToRender <=
        startingRenderRank + galleryButtons.length
      ) {
        changedGalleryButtons = galleryButtons.slice(
          0,
          this.state.itemsToRender - startingRenderRank
        );
      }
    }
    return changedGalleryButtons;
  }

  private renderGallerySections(
    id: string,
    gallerySections: AppFullGallerySection<T>[],
    positionInSet?: number,
    level?: number
  ): JSX.Element {
    const startingPositionInSet = !positionInSet ? 1 : positionInSet;
    let sectionsToRender = gallerySections;

    const sectionStartingRenderRank: number[] = [];
    if (
      this.props.isShadowRendering &&
      this.state.itemsToRender !== undefined
    ) {
      let count: number = 0;
      for (let i: number = 0; i < gallerySections.length; i = i + 1) {
        sectionStartingRenderRank[i] = count;
        count += gallerySections[i].galleryButtons.length;
      }

      sectionsToRender = gallerySections.filter(
        (_, index) =>
          sectionStartingRenderRank[index] < this.state.itemsToRender!
      );
    }

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <div
            key={`${id}-galleryWrapper`}
            data-no-vertical-wrap={true}
            data-no-horizontal-wrap={false}
          >
            {sectionsToRender.map((section, index) => {
              const idTitle = section.title
                ? section.title.replace(/[ ]/g, "-")
                : "";
              const fullTitleId = `title-${this.props.id}-${idTitle}`;
              return (
                <div
                  key={`${id}-gallerySectionWrapper${index}`}
                  aria-labelledby={section.title ? fullTitleId : undefined}
                >
                  {section.topDivider &&
                    this.renderDivider(theme, index, "top")}
                  {section.title &&
                    this.renderSectionTitle(
                      theme,
                      index,
                      section.title,
                      level,
                      fullTitleId
                    )}
                  {this.renderGalleryButtons(
                    section.galleryButtons,
                    this.props.isShadowRendering &&
                      this.state.itemsToRender !== undefined
                      ? sectionStartingRenderRank[index]
                      : undefined,
                    index + startingPositionInSet,
                    section.ariaLabel
                  )}
                  {section.bottomDivider &&
                    this.renderDivider(theme, index, "bottom")}
                </div>
              );
            })}
          </div>
        )}
      </AppThemeConsumer>
    );
  }

  private renderDivider(
    theme: ITheme,
    index: number,
    location: string
  ): JSX.Element {
    const styles = getAppGalleryStyles(theme, this.props.styles);

    return (
      <div
        key={`separator-${index}-${location}`}
        className={mergeStyles(styles.expandedGallerySectionDivider)}
        role="separator"
      />
    );
  }
  private renderSectionTitle(
    theme: ITheme,
    index: number,
    title: string,
    level: number = 1,
    idTitle: string
  ): JSX.Element {
    const styles = getAppGalleryStyles(theme, this.props.styles);

    return (
      <div
        id={idTitle}
        key={`title${index}`}
        className={mergeStyles(styles.expandedGallerySectionTitle)}
        role="heading"
        aria-level={level}
      >
        <span
          className={mergeStyles(styles.expandedGallerySectionTitleInnerSpan)}
        >
          {title}
        </span>
      </div>
    );
  }

  /**
   * Render a Gallery Button. Passed to Grid as a prop.
   * @param galleryButton - The galleryButton to render
   * @returns {JSX.Element} - Element representing the gallery button
   */
  private renderGalleryButton = (galleryButton: T): JSX.Element => {
    const GalleryButtonTag =
      this.props.getGalleryButtonComponent || AppGalleryButton;
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const gallerybuttonProps = {
            ...galleryButton,
            onFocus: this.props.onGalleryButtonFocus,
            onExecute: this.onGalleryButtonExecute,
            style: getAppGalleryStyles(
              theme,
              getMergedGalleryAndButtonStyles(
                this.props.styles,
                galleryButton.style
              )
            ).galleryButtonInExpanded,
            role: galleryButton.role || "menuitemradio",
            focusOnHover: true,
            onMouseEnter: this.onMouseEnter,
            onMouseLeave: this.onMouseLeave,
            onWheel: this.onWheel,
            onMouseMove: this.onMouseMove,
            onKeyDown: this.onKeydown,
            // Task 2547546
            // Currently shouldTakeFocus will always be undefined, however we need to do major refactoring
            // to set shouldTakeFocus to the GalleryButton that we might not need to do.
            shouldKeepFocus:
              galleryButton.shouldTakeFocus !== undefined
                ? galleryButton.shouldTakeFocus
                : true,
            contextMenuMap: this.props.contextMenuMap,
          };
          return (
            <GalleryButtonTag {...gallerybuttonProps} /> || (
              <AppGalleryButton {...gallerybuttonProps} />
            )
          );
        }}
      </AppThemeConsumer>
    );
  };

  onMouseMove = (ev: React.MouseEvent<HTMLButtonElement>): void => {
    const targetElement = ev.currentTarget as HTMLElement;

    // If navigation is not idle, the targetElement is the focused element, or we are currently in this gallery's context menu bail out
    if (
      !this.isNavigationIdle ||
      (document && targetElement === (document.activeElement as HTMLElement)) ||
      this.isFocusInContextMenu()
    ) {
      return;
    }

    targetElement.focus();
  };

  private onMouseEnter = (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (
      this.isNavigationIdle &&
      !this.props.disabled &&
      !this.isFocusInContextMenu()
    ) {
      ev.currentTarget.focus();
    }
  };

  private onMouseLeave = (ev: React.MouseEvent<HTMLButtonElement>): void => {
    if (
      this.isNavigationIdle &&
      !this.props.disabled &&
      !this.isFocusInContextMenu()
    ) {
      // Get the parent menu of the target and focus it
      const elements = document.querySelectorAll('[tabindex="0"][role="menu"]');

      for (let index = 0; index < elements.length; index += 1) {
        if (elements[index].contains(ev.currentTarget)) {
          /**
           * IE11 focus() method forces parents to scroll to top of element.
           * Edge and IE expose a setActive() function for focusable divs that
           * sets the page focus but does not scroll the parent element.
           */
          if ((elements[index] as any).setActive) {
            (elements[index] as any).setActive();
          } else {
            (elements[index] as HTMLElement).focus();
          }

          break;
        }
      }
    }
  };

  private onWheel = () => {
    this.setNavigationTimeout();
  };

  private onKeydown = (ev: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (
      ev.which === KeyCodes.up ||
      ev.which === KeyCodes.down ||
      ev.which === KeyCodes.left ||
      ev.which === KeyCodes.right
    ) {
      this.setNavigationTimeout();
    }
  };

  /**
   * Sets a timeout so we won't process any mouse "hover" events
   * while navigating (via mouseWheel or arrowKeys)
   */
  private setNavigationTimeout = () => {
    if (!this.isNavigationIdle && this.navigationIdleTimeoutId !== undefined) {
      this.async.clearTimeout(this.navigationIdleTimeoutId);
      this.navigationIdleTimeoutId = undefined;
    } else {
      this.isNavigationIdle = false;
    }

    this.navigationIdleTimeoutId = this.async.setTimeout(() => {
      this.isNavigationIdle = true;
    }, this.navigationIdleDelay);
  };

  /**
   * Handle the click on a gallery button
   * @param galleryButton - The gallery button that the click was fired against
   */
  private onGalleryButtonExecute = (
    parameter: AppGalleryButtonOnExecuteParameter<T>
  ) => {
    const { galleryButton, inputMethod, shouldTakeFocus, triggerTime } =
      parameter;
    // Clear any effect of having been focusing on
    if (this.props.onGalleryButtonFocus) {
      this.props.onGalleryButtonFocus();
    }

    const index = galleryButton.index;
    // If we have a valid index and it is not already
    // selected, select it
    if (index !== undefined && index >= 0) {
      if (this.props.onGalleryButtonExecute) {
        this.props.onGalleryButtonExecute({
          galleryButton,
          location: this.props.location,
          inputMethod,
          shouldTakeFocus,
          triggerTime,
        });
      }

      this.setState({
        selectedId: galleryButton.id,
      });
    }
  };

  private isFocusInContextMenu = () => {
    return (
      this.props.contextMenuMap?.primaryMenuId &&
      elementContains(
        document.getElementById(this.props.contextMenuMap?.primaryMenuId),
        document.activeElement as HTMLElement
      )
    );
  };
}

/**
 * Injecting the shadow rendering context props.
 * Externally, you do not need to care about this at all. Shadow rendering is just
 * an implementation detail of this component.
 */
export const AppFullGallery: {
  <T extends AppGalleryButtonProps>(props: AppFullGalleryProps<T>): JSX.Element;
} = withShadowRendering<any>(AppFullGalleryBase);
